import { useState } from 'react';
import cardAccessService from 'common/services/card-access-service/card-access.service';
import { GateOperationEnum, RelayOperationEnum } from 'common/services/card-access-service/card-access.service.types';
import { notification } from 'common/utils/notification';

export const useGateControls = () => {
  const [buttonsState, setButtonsState] = useState<Record<string, boolean>>({});

  const formatRelayKey = (panelNumber: string, relayNumber: string, operation: RelayOperationEnum) =>
    `${panelNumber}:${relayNumber}:${operation}:relay`;

  const formatGateKey = (panelNumber: string, readerNumber: string, operation: GateOperationEnum) =>
    `${panelNumber}:${readerNumber}:${operation}:reader`;

  const updateButtonsState = (panelNumber: string, number: string, targetKey: string, resetAfterOneSecond: boolean) => {
    setButtonsState((prev) => {
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        const [previousPanel, previousNumber] = key.split(':');
        newState[key] = previousPanel === panelNumber && previousNumber === number ? false : prev[key];
      });
      newState[targetKey] = true;
      return newState;
    });

    if (resetAfterOneSecond) {
      setTimeout(() => {
        setButtonsState((prev) => ({ ...prev, [targetKey]: false }));
      }, 1000);
    }
  };

  const runOperation = async (toggle: () => Promise<unknown>, update: () => void) => {
    try {
      await toggle();
      notification.success({ message: 'Completed', duration: 5 });
      update();
    } catch (error: any) {
      if (error.response?.data?.errorCode === 'CannotToggleGate') {
        notification.destroy();
        notification.error({ message: error.response?.data.message });
        return;
      }
      throw error;
    }
  };

  const onRelayClick = (panelNumber: string, relayNumber: string, operation: RelayOperationEnum) =>
    runOperation(
      () => cardAccessService.toggleRelay(panelNumber, relayNumber, operation),
      () =>
        updateButtonsState(
          panelNumber,
          relayNumber,
          formatRelayKey(panelNumber, relayNumber, operation),
          operation === RelayOperationEnum.EnergizeTimeOverride,
        ),
    );

  const onGateClick = (panelNumber: string, readerNumber: string, operation: GateOperationEnum) =>
    runOperation(
      () => cardAccessService.toggleGate(panelNumber, readerNumber, operation),
      () =>
        updateButtonsState(
          panelNumber,
          readerNumber,
          formatGateKey(panelNumber, readerNumber, operation),
          operation === GateOperationEnum.Unlock,
        ),
    );

  return { buttonsState, formatRelayKey, formatGateKey, onRelayClick, onGateClick };
};

export type GateControls = ReturnType<typeof useGateControls>;
